import React, { FC, useEffect, useState } from 'react'
import { ScrollView, TouchableOpacity, View } from 'react-native'
import { useNavigate, useParams } from 'react-router-dom'

import { AppBackground, ResponsiveLogo } from '@ui/components'
import { travelApi, TravelLogic, type ITravelData } from '@front/logic'
import { getHomeInfo } from '@front/apis'

import { BodyText, Headline2, Headline4, colors } from '@/aena/styles'
import { openStoredDocument } from './docUtils'

// Cabecera estandar de la app: el MISMO ResponsiveLogo + HeaderWithBackAena que usa
// /travel (list.tsx), para que el logo se vea al mismo tamano que en las demas paginas.
import { ReactComponent as AenaDogIcon } from '@/assets/icons/aena/aena_dog.svg'
import { HeaderWithBackAena } from '@/components/headerwithback'

const navy = colors.azulCorporativo
const verde = colors.aenaVerde

// Logo oficial de Air Europa (sustituye el texto "Boarding Check IN : Air Europa").
const AirEuropaLogo = require('@/assets/icons/air_europa.jpg')

// Estados que representan un viaje "validado" (ver TravelLogic / toUiStatus en
// aenaTravelCheckinNeon: CONFIRMED/APPROVED/ACCEPTED -> 'confirmed').
export const VALIDATED_TRAVEL_STATUSES: ITravelData['status'][] = ['confirmed', 'completed']

export const isTravelValidated = (status?: string): boolean =>
  !!status && VALIDATED_TRAVEL_STATUSES.includes(status as ITravelData['status'])

// El codigo de aerolinea de la maqueta ("UX") no se expone en ITravelData (el campo
// airline es el nombre, ej. "Air Europa"). Lo derivamos a partir del nombre.
const airlineCodeFromName = (airline: string): string => {
  const name = (airline || '').toLowerCase()
  if (name.includes('air europa') || name === 'ux') return 'UX'
  // Fallback: si ya viniera un codigo de 2 letras lo usamos en mayusculas.
  const code = (airline || '').trim()
  return /^[a-z]{2}$/i.test(code) ? code.toUpperCase() : code
}

const formatFlightDate = (dateStr: string): string => {
  // dd/MM/yyyy como en la maqueta.
  try {
    const date = new Date(dateStr)
    if (Number.isNaN(date.getTime())) return dateStr
    const dd = String(date.getDate()).padStart(2, '0')
    const mm = String(date.getMonth() + 1).padStart(2, '0')
    const yyyy = date.getFullYear()
    return `${dd}/${mm}/${yyyy}`
  } catch {
    return dateStr
  }
}

// Fila etiqueta (negrita) + valor. Centrada y con tipografia mas grande.
const DataRow: FC<{ label: string; value: string }> = ({ label, value }) => (
  <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
    <BodyText style={{ fontWeight: '700', color: navy, fontSize: 19, paddingLeft: 0 }}>{label} </BodyText>
    <BodyText style={{ color: navy, fontSize: 19, paddingLeft: 0 }}>{value}</BodyText>
  </View>
)

// Fila con enlace "Ok" verde subrayado clicable. Centrada y mas grande.
const DataLinkRow: FC<{ label: string; onPress: () => void }> = ({ label, onPress }) => (
  <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
    <BodyText style={{ fontWeight: '700', color: navy, fontSize: 19, paddingLeft: 0 }}>{label} </BodyText>
    <TouchableOpacity onPress={onPress}>
      <BodyText style={{ color: verde, fontWeight: '700', textDecorationLine: 'underline', fontSize: 19, paddingLeft: 0 }}>
        Ok
      </BodyText>
    </TouchableOpacity>
  </View>
)

export const TravelBoarding: FC = () => {
  const navigate = useNavigate()
  const { travelId } = useParams<{ travelId: string }>()

  const [travel, setTravel] = useState<ITravelData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [petAvatars, setPetAvatars] = useState<Record<string, string>>({})

  useEffect(() => {
    const loadTravelDetail = async () => {
      if (!travelId) return
      try {
        setLoading(true)
        setError(null)
        // Datos reales del check-in via getTravelDetailFromNeon (travelApi.getTravelDetail).
        const response = await travelApi.getTravelDetail({ travelId })
        setTravel(response.data)
      } catch (err) {
        console.error('Error loading boarding check-in:', err)
        setError('Error al cargar el check-in del viaje')
      } finally {
        setLoading(false)
      }
    }

    const loadPetAvatars = async () => {
      try {
        const home = await getHomeInfo({ lat: 0, lon: 0 })
        const map: Record<string, string> = {}
        home.data?.petList?.forEach((p: any) => { if (p?.guid) map[p.guid] = p.avatar })
        setPetAvatars(map)
      } catch (e) {
        console.debug('Error loading pet avatars:', e)
      }
    }

    loadTravelDetail()
    loadPetAvatars()
  }, [travelId])

  if (loading) {
    return (
      <AppBackground isSafe>
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <BodyText>Cargando check-in...</BodyText>
        </View>
      </AppBackground>
    )
  }

  if (error || !travel) {
    return (
      <AppBackground isSafe>
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 }}>
          <BodyText style={{ color: colors.rojoInactivo, marginBottom: 16 }}>
            {error || 'Viaje no encontrado'}
          </BodyText>
          <TouchableOpacity
            onPress={() => navigate(-1)}
            style={{
              paddingVertical: 12,
              paddingHorizontal: 20,
              borderRadius: 8,
              backgroundColor: verde,
            }}
          >
            <BodyText style={{ color: colors.blanco, fontWeight: '600' }}>Volver</BodyText>
          </TouchableOpacity>
        </View>
      </AppBackground>
    )
  }

  const petName = travel.pets?.[0]?.name || ''
  const petAvatar = travel.pets?.[0]?.guid ? petAvatars[travel.pets[0].guid] : undefined

  // FIX doble codigo "UX UX1234": si flightNumber ya empieza por 2-3 letras (el
  // codigo de aerolinea, p.ej. "UX1234"), lo usamos tal cual; si no, anteponemos el
  // codigo de aerolinea (airlineCode o el derivado del nombre).
  const flightLabel = (): string => {
    const fn = (travel.flightNumber || '').trim()
    if (/^[a-z]{2,3}/i.test(fn)) return fn
    const code = (travel.airlineCode || airlineCodeFromName(travel.airline) || '').trim()
    return `${code} ${fn}`.trim()
  }

  // URL del documento guardado por tipo (liability_release, full_report...).
  const docUrl = (type: string): string | null =>
    travel.documents?.find((d) => d.type === type)?.url ?? null

  return (
    <AppBackground isSafe>
      <View style={{ flex: 1, maxWidth: 800, width: '100%', alignSelf: 'center' }}>
        {/* Cabecera estandar identica a /travel: ResponsiveLogo (mismo tamano) + back. */}
        <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', padding: 16, gap: 16 }}>
          <ResponsiveLogo
            breakpoint={820}
            hysteresis={20}
            large={{ width: 680, height: 96 }}
            small={{ width: 360, height: 48 }}
          />
        </View>

        <HeaderWithBackAena title="Tarjeta de embarque" navigateTo="/aena/travel" />

        <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 20, paddingBottom: 32 }}>
          {/* Avatar de la mascota con borde verde + nombre */}
          <View style={{ alignItems: 'center', marginBottom: 16 }}>
            <View style={{
              width: 96,
              height: 96,
              borderRadius: 48,
              borderWidth: 3,
              borderColor: verde,
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              backgroundColor: colors.grisClaro,
            }}>
              {petAvatar ? (
                <img
                  src={petAvatar}
                  alt={petName}
                  style={{ width: 90, height: 90, borderRadius: 45, objectFit: 'cover' }}
                />
              ) : (
                <AenaDogIcon width={56} height={56} />
              )}
            </View>
            {!!petName && (
              <Headline4 style={{ marginTop: 8 }}>{petName}</Headline4>
            )}
          </View>

          {/* Titulo en negrita */}
          {/* Logo oficial de Air Europa, centrado (sustituye el texto). */}
          <View style={{ alignItems: 'center', marginTop: 4, marginBottom: 18 }}>
            <img src={AirEuropaLogo} alt="Air Europa" style={{ width: 260, height: 'auto', objectFit: 'contain', maxWidth: '90%' }} />
          </View>

          {/* Circulo verde grande con check blanco (validado) */}
          <View style={{ alignItems: 'center', marginBottom: 24 }}>
            <View style={{
              width: 88,
              height: 88,
              borderRadius: 44,
              backgroundColor: verde,
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <BodyText style={{ color: colors.blanco, fontSize: 48, fontWeight: '700', paddingLeft: 0 }}>
                {'✓'}
              </BodyText>
            </View>
          </View>

          {/* Bloque de datos */}
          <View style={{ paddingHorizontal: 8 }}>
            <DataRow label="Fecha :" value={formatFlightDate(travel.flightDate)} />
            <DataRow label="Vuelo:" value={flightLabel()} />
            <DataRow label="Localizador:" value={travel.locator} />
            <DataLinkRow label="Descargo Responsabilidad:" onPress={() => openStoredDocument(docUrl('liability_release'))} />
            <DataLinkRow label="Certificado PetPass:" onPress={() => openStoredDocument(docUrl('full_report'))} />
          </View>
        </ScrollView>
      </View>
    </AppBackground>
  )
}

export default TravelBoarding
